import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, LayoutGrid, List, Search } from 'lucide-react';
import { portalOrders } from '../../api/resources';
import { inr, fmtDate } from '../../utils/format';
import PageHeader from '../../components/ui/PageHeader';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/Badge';
import { WORK_LABEL, isPastDay } from './shared';

// What is waiting, what goes out today, what is late, what is done — and
// everything, for when they just want to look.
const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'today', label: 'Due today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All' },
];
// Overdue is the one that should catch the eye when it is not empty.
const TAB_TONE = {
  overdue: 'bg-danger-600 text-white',
  today: 'bg-warning-600 text-white',
};

const LAYOUT_KEY = 'erp_portal_layout';
const readLayout = () => {
  try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'cards'; } catch { return 'cards'; }
};

function DueDate({ iso, done }) {
  if (!iso) return <span className="text-content-subtle">—</span>;
  const late = !done && isPastDay(iso);
  return <span className={late ? 'font-semibold text-danger-600' : ''} title={late ? 'Delivery date has passed' : undefined}>{fmtDate(iso)}</span>;
}

// One compact line per job, e.g. "Shirt × 2 · Cutting".
const jobLines = (o) => o.items.flatMap((it) => it.jobs.map((j) => ({
  key: j.assignmentId,
  text: `${it.itemName} × ${it.qty} · ${WORK_LABEL[j.workType]}`,
  status: j.status,
  settled: j.settled,
})));

function OrderCard({ o }) {
  const done = o.myStatus === 'COMPLETED';
  const started = o.myStatus === 'IN_PROGRESS';
  return (
    <Link
      to={`/portal/orders/${o.invoiceId}`}
      className={`card block border-l-4 p-4 transition-shadow hover:shadow-pop ${
        done
          ? 'border-l-success-500 bg-surface-sunken opacity-90'
          : started
            ? 'border-l-warning-500'
            : 'border-l-brand-500'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`flex items-center gap-1.5 font-semibold ${done ? 'text-content-muted' : 'text-brand-700'}`}>
            {done && <CheckCircle2 size={15} className="shrink-0 text-success-600" />}
            {o.orderNo}
          </p>
          <p className={`truncate text-sm ${done ? 'text-content-muted' : 'text-content'}`}>{o.customerName || '—'}</p>
        </div>
        {done ? (
          <span className="shrink-0 rounded-full bg-success-50 px-2 py-0.5 text-2xs font-bold uppercase tracking-wide text-success-700">
            Done
          </span>
        ) : (
          <StatusBadge status={o.myStatus} />
        )}
      </div>

      {/* A finished job has no deadline left to worry about. */}
      {done ? (
        <p className="mt-3 text-xs text-content-subtle">
          Finished{o.deliveryDate ? ` · delivery was ${fmtDate(o.deliveryDate)}` : ''}
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div><p className="text-content-subtle">Delivery</p><DueDate iso={o.deliveryDate} done={false} /></div>
          <div><p className="text-content-subtle">Trial</p>{o.trialDate ? fmtDate(o.trialDate) : '—'}</div>
        </div>
      )}

      <ul className="mt-3 space-y-1 border-t border-line pt-3 text-xs">
        {jobLines(o).map((j) => (
          <li key={j.key} className="flex items-center justify-between gap-2">
            <span className={`truncate ${done ? 'text-content-subtle' : 'text-content-muted'}`}>{j.text}</span>
            <StatusBadge status={j.settled ? 'SETTLED' : j.status} />
          </li>
        ))}
      </ul>

      <p className="mt-3 text-right text-sm">
        <span className="text-content-subtle">{done ? 'Earned ' : 'My earnings '}</span>
        <b className={`tabular-nums ${done ? 'text-success-700' : 'text-brand-900'}`}>{inr(o.myEarnings)}</b>
      </p>
    </Link>
  );
}

export default function PortalOrders() {
  const nav = useNavigate();
  const [view, setView] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get('view');
    return ['pending', 'today', 'overdue', 'completed', 'all'].includes(asked) ? asked : 'pending';
  });
  const [layout, setLayoutState] = useState(readLayout);
  const [search, setSearch] = useState('');

  const setLayout = (l) => {
    setLayoutState(l);
    try { localStorage.setItem(LAYOUT_KEY, l); } catch { /* storage unavailable */ }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['portal-orders', view, search],
    queryFn: () => portalOrders({ view, ...(search.trim() ? { search: search.trim() } : {}) }),
  });
  const orders = data?.orders || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-4">
      <PageHeader icon={ClipboardList} eyebrow="My Work" title="My Orders" subtitle="Only the items assigned to you are shown" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-line bg-surface p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === t.key
                  ? (TAB_TONE[t.key] || 'bg-brand-700 text-white')
                  : `text-content-muted hover:text-content ${t.key === 'overdue' && (counts.overdue || 0) > 0 ? 'text-danger-600' : ''}`
              }`}
            >
              {t.label}
              <span className={`ml-1.5 rounded px-1.5 text-2xs ${view === t.key ? 'bg-white/20' : 'bg-surface-sunken'}`}>{counts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="relative ml-auto w-56">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input className="input input-sm pl-8" placeholder="Order, item or customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex rounded-lg border border-line bg-surface p-0.5">
          <button className={`btn-icon h-7 w-7 ${layout === 'cards' ? 'bg-surface-hover text-brand-700' : ''}`} onClick={() => setLayout('cards')} title="Cards" aria-label="Card layout"><LayoutGrid size={15} /></button>
          <button className={`btn-icon h-7 w-7 ${layout === 'list' ? 'bg-surface-hover text-brand-700' : ''}`} onClick={() => setLayout('list')} title="List" aria-label="List layout"><List size={15} /></button>
        </div>
      </div>

      {isLoading && <Spinner />}
      {!isLoading && orders.length === 0 && (
        <div className="card">
          <EmptyState
            title="Nothing here"
            hint={{
              completed: 'Work you have finished will show up here.',
              today: 'Nothing of yours is due today.',
              overdue: 'Nothing of yours is late — good.',
              pending: 'No work is waiting on you right now.',
            }[view] || 'No work is assigned to you right now.'}
          />
        </div>
      )}

      {!isLoading && orders.length > 0 && layout === 'cards' && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {orders.map((o) => <OrderCard key={o.invoiceId} o={o} />)}
        </div>
      )}

      {!isLoading && orders.length > 0 && layout === 'list' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Order</th>
                <th className="th">Customer</th>
                <th className="th">My Work</th>
                <th className="th">Delivery</th>
                <th className="th">Status</th>
                <th className="th text-right">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.invoiceId} className="row cursor-pointer" onClick={() => nav(`/portal/orders/${o.invoiceId}`)}>
                  <td className="td font-semibold text-brand-700">{o.orderNo}</td>
                  <td className="td">{o.customerName || '—'}</td>
                  <td className="td text-xs text-content-muted">{jobLines(o).map((j) => j.text).join(', ')}</td>
                  <td className="td"><DueDate iso={o.deliveryDate} done={o.myStatus === 'COMPLETED'} /></td>
                  <td className="td"><StatusBadge status={o.myStatus} /></td>
                  <td className="td text-right font-semibold tabular-nums">{inr(o.myEarnings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
